// @adr SPARK-0055-document-displays-use-one-public-boundary@3: The Match PDF generator keeps its isolated renderer; existing file previews use DocumentView.
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IntlShape } from "react-intl";
import type { RankSortBy, RankThresholds } from "./match-result-derive";
import {
  buildMatchResultPdfRank,
  buildMatchResultPdfRankForCompanies,
} from "@/lib/match-result-pdf-rank";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { Disclosure } from "@/components/design/Disclosure";
import { Switch } from "@/components/design/Switch";
import { CompanyStatus } from "@/graphql/generated/types";
import { ELEMENT_CODES } from "@/lib/elements";
import {
  DEFAULT_MATCH_RESULT_PDF_SECTIONS,
  effectiveSectionsForMode,
  sectionsForMode,
  type MatchResultPdfAgent,
  type MatchResultPdfPayload,
  rankClusterLabel,
  type MatchResultPdfRankCandidate,
  type MatchResultPdfPersonDetail,
  type MatchResultPdfPowersMatrix,
  type MatchResultPdfRequirementMatrix,
  type MatchResultPdfRequirementRow,
  type MatchResultPdfSection,
  type MatchResultPdfSectionSettings,
} from "@/lib/match-result-pdf-model";
import {
  isMatchResultRendererResponse,
  matchResultRendererDocumentUrl,
  type MatchResultRendererRequest,
} from "@/lib/match-result-renderer-protocol";
import {
  buildCompanyRanking,
  criterionFormatter,
  type CompanyRankingRowInput,
  type CriterionFormatter,
} from "./company-ranking-derive";
import { displayAgent } from "./agents";
import { useAgentsByIds } from "./use-agent-search";
import type { AgentListRow } from "./agents";
import { useRequirementsReferenceData } from "./use-requirements-reference-data";
import { DEFAULT_REQUIREMENT_LENIENCY, parseDesign } from "./design-codec";
import {
  buildBoosterCoverage,
  buildClusterCarriers,
  buildClusterWishes,
  buildFactSkillFulfillment,
  criterionKindOrder,
  DEFAULT_RANK_THRESHOLDS,
  buildHeatmapRows,
  buildMemberDetail,
  buildPowerClusters,
  buildPowerColumnGroups,
  buildPowersCoverage,
  buildPowerTilts,
  buildRequirementAnalysis,
  buildSkillCoverage,
  compositionNote,
  constraintSummary,
  labelForCondition,
  wishTarget,
  type ConditionLabelFormatters,
  type ConditionLabelLookup,
  type RequirementAnalysis,
  type ResultPerson,
} from "./match-result-derive";
import {
  formatConditionActual,
  useConditionLabelFormatters,
  useConditionLabelLookup,
} from "./result-panel-labels";
import { parseTeamResult } from "./match-result-codec";
import {
  matchModeLabelId,
  resolveMatchMode,
  type UiMatchMode,
} from "./match-mode";
import type { MatchData } from "./match-design-types";
import type { MatchResultDetailNode, SolverResult } from "./use-match-editor";

const STORAGE_KEY = "spark.matchResultPdfExport.v1";

const SECTION_LABELS: Record<MatchResultPdfSection, { id: string; defaultMessage: string }> = {
  summary: { id: "match.pdf.settings.summary", defaultMessage: "Summary" },
  team: { id: "match.pdf.settings.team", defaultMessage: "Team" },
  agents: { id: "match.result.agentsTitle", defaultMessage: "Agents" },
  requirements: { id: "match.pdf.settings.requirements", defaultMessage: "Requirements" },
  powers: { id: "match.pdf.settings.powers", defaultMessage: "Power clusters" },
  powerDetail: { id: "match.pdf.settings.powerDetail", defaultMessage: "Power detail" },
  ranking: { id: "match.pdf.settings.ranking", defaultMessage: "Ranking" },
  dataQuality: { id: "match.pdf.settings.dataQuality", defaultMessage: "Data quality" },
  notes: { id: "match.pdf.settings.notes", defaultMessage: "Notes" },
  personDetails: { id: "match.pdf.personDetails", defaultMessage: "Person details" },
  matrices: { id: "match.pdf.settings.matrices", defaultMessage: "Detailed matrices" },
};

// In der Rangliste heissen zwei Abschnitte anders, weil ihre Namen dort schon vergeben sind:
// "Personen-Details" ist auf diesem Schirm eine Kriterien-Kategorie, und "Matrizen" (Plural)
// verspricht die Powers-Matrix mit, die es im Rangliste-Bericht nicht gibt.
const RANK_SECTION_LABELS: Partial<Record<MatchResultPdfSection, { id: string; defaultMessage: string }>> = {
  personDetails: { id: "match.pdf.rank.candidateDetail", defaultMessage: "Candidate detail" },
  matrices: { id: "match.pdf.rank.criteriaMatrix", defaultMessage: "Criteria matrix" },
};

// Dieselben Abschnitte, anderer Gegenstand: nur die beiden, die ihn beim Namen nennen.
const COMPANY_SECTION_LABELS: Partial<Record<MatchResultPdfSection, { id: string; defaultMessage: string }>> = {
  ranking: { id: "match.pdf.companyRanking", defaultMessage: "Company ranking" },
  personDetails: { id: "match.pdf.rank.companyDetail", defaultMessage: "Company detail" },
  matrices: { id: "match.pdf.rank.criteriaMatrix", defaultMessage: "Criteria matrix" },
};

function sectionLabel(section: MatchResultPdfSection, mode: UiMatchMode) {
  const perMode =
    mode === "RANK"
      ? RANK_SECTION_LABELS[section]
      : mode === "RANK_COMPANIES"
        ? COMPANY_SECTION_LABELS[section]
        : undefined;
  return perMode ?? SECTION_LABELS[section];
}

// Wie viele Kriterien eine Person im Detail zeigt, bevor "+N weitere" uebernimmt - gleicher
// Deckel wie die eingeklappte UI-Ansicht (MEMBER_CRITERIA_CAP in ResultPanel). Offene Muss
// kommen zusaetzlich dazu, die duerfen nie unter den Deckel fallen.
const PDF_MEMBER_CRITERIA_CAP = 5;
// Drei Highlights je Spalte statt der halben Inventarliste - die Zusammenfassung bleibt eine
// Zusammenfassung (die UI zeigt fuenf und bietet einen Aufklapper, den ein PDF nicht hat).
const PDF_ANALYSIS_ROW_CAP = 3;

type MatchResultSourceTeam = NonNullable<NonNullable<MatchResultDetailNode["teams"]>[number] | NonNullable<SolverResult["teams"]>[number]>;
type MatchResultSourcePerson = NonNullable<NonNullable<MatchResultSourceTeam["persons"]>[number]>;
type MatchResultSourceCompany = NonNullable<NonNullable<MatchResultDetailNode["companies"]>[number]>;

export type MatchResultPdfSourceResult = {
  id: string;
  name?: string | null;
  notes?: string | null;
  kept?: boolean | null;
  design?: Record<string, unknown> | null;
  createdAt?: string | null;
  candidateCount?: number | null;
  teams?: MatchResultSourceTeam[] | null;
  companies?: MatchResultSourceCompany[] | null;
};

function readStoredSettings(): { sections: MatchResultPdfSectionSettings } {
  const empty = { sections: DEFAULT_MATCH_RESULT_PDF_SECTIONS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<{
      sections: Partial<MatchResultPdfSectionSettings>;
    }>;
    return { sections: { ...DEFAULT_MATCH_RESULT_PDF_SECTIONS, ...(parsed.sections ?? {}) } };
  } catch {
    return empty;
  }
}

function storeSettings(sections: MatchResultPdfSectionSettings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ sections }));
  } catch {
    // Export settings are convenience-only. A blocked storage backend must not break Result.
  }
}

function fullName(p: MatchResultSourcePerson): string {
  const given = Array.isArray(p.name?.given) ? p.name?.given : [p.name?.given];
  return [...(given ?? []), p.name?.family].filter(Boolean).join(" ") || p.formattedName || "?";
}

function slugPart(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

function fileName(date: Date, title: string): string {
  const slug = slugPart(title);
  const stamp = date.toISOString().replace(/[-:]/g, "").slice(0, 15);
  return `match-result${slug ? `-${slug}` : ""}-${stamp}.pdf`;
}

// Eine Anforderungszeile der Zusammenfassung. Die Auflage ("min. 4 im Team") reist als
// {id, values} weiter, den Namen des Alleintraegers loest nameOf schon hier auf - im
// Renderer gibt es die Personenliste nicht mehr.
function analysisRow(
  a: RequirementAnalysis,
  label: (a: RequirementAnalysis) => string,
  nameOf: (id: string) => string,
): MatchResultPdfRequirementRow {
  return {
    label: label(a),
    isMust: a.isMust,
    met: a.met,
    teamSize: a.teamSize,
    close: a.close,
    composition: compositionNote(a),
    soleCarrierName:
      a.fulfilled && a.met === 1 && a.metPersonIds[0] ? nameOf(a.metPersonIds[0]) : null,
  };
}

// Das Personen-Detail, so wie MemberDetailPanel es zeigt: Befundsatz, gedeckelte Kriterienliste,
// Cluster gegen das Team, Charakter. Sortierung und Deckel sind bewusst dieselben wie in der UI -
// ein PDF, das andere fuenf Kriterien zeigt als der Bildschirm, liest sich wie ein zweites Ergebnis.
function memberDetail(
  detail: NonNullable<ReturnType<typeof buildMemberDetail>>,
  intl: IntlShape,
  label: (condition: unknown) => string,
): MatchResultPdfPersonDetail {
  const hasPowers = detail.clusters.some((c) => c.assessed > 0);
  const criticalMust = detail.criticalMustFor.map(label);
  const criticalOptional = detail.criticalFor
    .filter((c) => !detail.criticalMustFor.includes(c))
    .map(label);
  const exclusiveRest = detail.exclusiveRequestedSkills.length - 3;
  const findings = [
    criticalMust.length > 0
      ? intl.formatMessage(
          { id: "match.result.memberSummaryCriticalMust" },
          { list: criticalMust.join(", "), count: criticalMust.length },
        )
      : null,
    criticalOptional.length > 0
      ? intl.formatMessage(
          { id: "match.result.memberSummaryCritical" },
          { list: criticalOptional.join(", ") },
        )
      : null,
    detail.exclusiveRequestedSkills.length > 0
      ? intl.formatMessage(
          { id: "match.result.memberSummaryExclusiveSkills" },
          {
            list: detail.exclusiveRequestedSkills.slice(0, 3).join(", "),
            rest: Math.max(0, exclusiveRest),
          },
        )
      : null,
    detail.topCoverage
      ? intl.formatMessage({ id: "match.result.memberSummaryTopCoverage" })
      : detail.noCoverage
        ? intl.formatMessage({
            id: hasPowers
              ? "match.result.memberSummaryNoCoverage"
              : "match.result.memberSummaryNoCoverageNoPowers",
          })
        : null,
    detail.topPowerFit ? intl.formatMessage({ id: "match.result.memberSummaryTopPowerFit" }) : null,
  ].filter((part): part is string => part !== null);

  // Erfuellt zuerst (kritische davon ganz oben), dann teilweise, dann offen; bei Gleichstand
  // Muss vor Kann. Identisch zu MemberDetailPanel.
  const sorted = [...detail.criteria].sort((x, y) => {
    const fx = x.fulfillment ?? 0;
    const fy = y.fulfillment ?? 0;
    if ((fy >= 1 ? 1 : 0) !== (fx >= 1 ? 1 : 0)) return (fy >= 1 ? 1 : 0) - (fx >= 1 ? 1 : 0);
    if (x.critical !== y.critical) return Number(y.critical) - Number(x.critical);
    if (fy !== fx) return fy - fx;
    return Number(y.requirement === "MUST") - Number(x.requirement === "MUST");
  });
  const head = sorted.slice(0, PDF_MEMBER_CRITERIA_CAP);
  const openMusts = sorted.filter((c) => c.requirement === "MUST" && (c.fulfillment ?? 0) < 1);
  const shown = [...head, ...openMusts.filter((m) => !head.includes(m))];

  return {
    verdict: findings.length > 0 ? findings.join(" ") : null,
    met: detail.requirements.met,
    total: detail.requirements.total,
    teamMedianMet: detail.requirements.teamMedianMet,
    criteria: shown.map((c) => ({
      label: label(c.condition),
      isMust: c.requirement === "MUST",
      state: !c.assessable
        ? ("unrated" as const)
        : (c.fulfillment ?? 0) >= 1
          ? ("met" as const)
          : (c.fulfillment ?? 0) > 0
            ? ("partial" as const)
            : ("open" as const),
    })),
    hiddenCriteria: Math.max(0, sorted.length - shown.length),
    // Gegen das eigene Team gelesen, groesster Vorsprung zuerst - wie die UI-Spalte.
    clusters: detail.clusters
      .filter((c) => c.score != null && c.teamScore != null)
      .map((c) => ({
        key: c.key,
        score: c.score,
        strong: c.strong,
        assessed: c.assessed,
        delta: (c.score as number) - (c.teamScore as number),
      }))
      .sort((a, b) => b.delta - a.delta)
      .map(({ key, score, strong, assessed }) => ({ key, score, strong, assessed })),
    powerFit: detail.powerFit,
    tilts: detail.tilts.map((t) => ({
      axis: t.axis,
      side: t.side,
      grade: t.grade,
      bothWeak: t.bothWeak,
    })),
    anchors: detail.anchors,
    soleAnchors: detail.soleAnchors,
  };
}

function buildPayload({
  data,
  result,
  locale,
  sections,
  selectedPersonIds,
  rankThresholds,
  rankSortBy,
  fallbackTitle,
  dataQualityLabels,
  intl,
  labelLookup,
  labelFormatters,
  companyFormat,
  liveAgents,
}: {
  data: MatchData;
  result: MatchResultPdfSourceResult;
  locale: string;
  sections: MatchResultPdfSectionSettings;
  selectedPersonIds: ReadonlySet<string> | null;
  rankThresholds: RankThresholds;
  rankSortBy: RankSortBy;
  companyFormat: CriterionFormatter;
  fallbackTitle: string;
  dataQualityLabels: {
    noRequirements: string;
    unknownRequirements: string;
    noPowers: string;
  };
  intl: IntlShape;
  labelLookup: ConditionLabelLookup;
  labelFormatters: ConditionLabelFormatters;
  /** The instance's agent rows by id; the label source for a stored agent that still exists. */
  liveAgents: ReadonlyMap<string, AgentListRow>;
}): MatchResultPdfPayload {
  const mode = resolveMatchMode(data.matchMode);
  const effectiveSections = effectiveSectionsForMode(mode, sections);
  const design = result.design ? parseDesign(result.design) : data;
  const companyMode = mode === "RANK_COMPANIES";
  const requirementsCount = companyMode
    ? (design.companyConditions ?? []).length
    : design.conditions.length;
  const unknownRequirementsCount = companyMode
    ? (design.unknownCompanyConditions ?? []).length
    : (design.unknownConditions ?? []).length;
  const elementWeights = design.teamProfile?.elementWeights ?? data.teamProfile?.elementWeights;
  const conditionLabel = (condition: unknown) =>
    labelForCondition(condition, labelLookup, labelFormatters);
  // RANK leitet nicht ueber Teams ab: die Team-Anforderungsanalyse ist auf Team-Komposition
  // zugeschnitten und liest die Composition-Operatoren eines RANK-Designs als Absicht, obwohl
  // der Editor dafuer in diesem Modus gar nicht sichtbar ist. `teams` bleibt deshalb leer und
  // `rank` traegt den Bericht (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3).
  const rankMode = mode === "RANK" || companyMode;
  const teams = rankMode ? [] : (result.teams ?? []).map((team) => {
    const persons: ResultPerson[] = (team.persons ?? [])
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => ({
        id: p.id,
        name: fullName(p),
        profession: p.profession ?? null,
      }));
    const view = parseTeamResult(team.result ?? null);
    const factSkillFulfillment = buildFactSkillFulfillment(view.factSkill, view.teamMatch);
    const powersCoverage = buildPowersCoverage(view.powers);
    const powerClusters = buildPowerClusters(view.powers);
    const nameOf = (id: string) => persons.find((p) => p.id === id)?.name ?? id;
    const rowLabel = (a: RequirementAnalysis) => conditionLabel(a.condition);

    // Staerken/Luecken exakt wie die Match-Analyse sortiert: Staerken breiteste zuerst,
    // Luecken verletztes Muss zuerst und dann groesste Luecke.
    const analysis = buildRequirementAnalysis(view.factSkill);
    const coverage = (a: RequirementAnalysis) => (a.teamSize > 0 ? a.met / a.teamSize : 0);
    const strengths = analysis.filter((a) => a.fulfilled).sort((x, y) => coverage(y) - coverage(x));
    const gaps = analysis
      .filter((a) => !a.fulfilled)
      .sort((x, y) => Number(y.isMust) - Number(x.isMust) || x.proximity - y.proximity);
    const musts = analysis.filter((a) => a.isMust);
    const kanns = analysis.filter((a) => !a.isMust);

    const wishes = buildClusterWishes(view.powers, elementWeights);
    const hasWishes = wishes.size > 0;

    return {
      teamId: team.teamId,
      persons: persons.map((p) => {
        const detail =
          effectiveSections.personDetails && effectiveSections.team
            ? buildMemberDetail(view.factSkill, view.powers, p.id, elementWeights)
            : null;
        return {
          id: p.id,
          name: p.name,
          profession: p.profession ?? null,
          detail: detail ? memberDetail(detail, intl, conditionLabel) : null,
        };
      }),
      factSkillPercent: factSkillFulfillment.totalCount > 0 ? factSkillFulfillment.totalPercent * 100 : null,
      powersPercent: view.powers?.totalPercent ?? null,
      powerGroups: powerClusters.map((cluster) => ({
        key: cluster.key,
        percent: cluster.score,
        assessedMembers: cluster.assessedMembers,
        target: hasWishes ? wishTarget(wishes.get(cluster.key) ?? 0, 100) : null,
      })),
      criteriaCount: factSkillFulfillment.totalCount,
      measuredPowerMembers: powersCoverage.measured,
      totalPowerMembers: powersCoverage.total,
      mustMet: musts.filter((a) => a.fulfilled).length,
      mustTotal: musts.length,
      kannMet: kanns.filter((a) => a.fulfilled).length,
      kannTotal: kanns.length,
      // Anforderungen, die auf genau einer Person ruhen: geht sie, faellt die Anforderung.
      soleRequirementsCount: analysis.filter((a) => a.fulfilled && a.met === 1).length,
      strengths: strengths.slice(0, PDF_ANALYSIS_ROW_CAP).map((a) => analysisRow(a, rowLabel, nameOf)),
      gaps: gaps.slice(0, PDF_ANALYSIS_ROW_CAP).map((a) => analysisRow(a, rowLabel, nameOf)),
      // Ein einzelner starker Traeger ist ein Einzelfall, keine Team-Faehigkeit - daher >= 2.
      extraSkills: buildSkillCoverage(view.factSkill)
        .filter((c) => !c.requested && c.strong >= 2)
        .slice(0, 3)
        .map((c) => c.skill),
      tilts: buildPowerTilts(view.powers).map((t) => ({
        axis: t.axis,
        side: t.side,
        grade: t.grade,
        bothWeak: t.bothWeak,
      })),
      clusterCarriers: buildClusterCarriers(view.powers).map((c) => ({
        key: c.key,
        carriers: c.carriers,
        signature: c.signature,
        assessed: c.assessed,
      })),
      boosterCoverage: buildBoosterCoverage(view.powers),
    };
  });
  const firstTeam = result.teams?.[0] ?? null;
  const firstTeamPersons: ResultPerson[] = (firstTeam?.persons ?? [])
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .map((p) => ({ id: p.id, name: fullName(p), profession: p.profession ?? null }));
  const firstView = parseTeamResult(firstTeam?.result ?? null);
  const companyRows: CompanyRankingRowInput[] = (result.companies ?? []).map((entry) => ({
    entryId: entry.entryId,
    position: entry.position,
    result: (entry.result as Record<string, unknown> | null) ?? null,
    company: entry.company
      ? {
          id: entry.company.id,
          name: entry.company.name,
          inactive: entry.company.status === CompanyStatus.Inactive,
          industryId: entry.company.industry?.id ?? null,
          employeeCount: entry.company.employeeCount ?? null,
        }
      : null,
  }));
  const rankedCompanies = companyMode ? buildCompanyRanking(companyRows) : [];
  // Anforderungs-Matrix: Personen x gefragte Kriterien, Zelle = erfuellt/nicht. Die Spalten
  // heissen im PDF R1..Rn und werden unter der Tabelle aufgeloest - 14 volle Kriteriennamen
  // passen nicht nebeneinander auf A4.
  // Aus buildRequirementAnalysis statt aus den Items neu abgeleitet: metPersonIds sagt bereits,
  // WER eine Anforderung erfuellt, und zwar nach derselben Klassifikation wie die Analyse oben.
  // Eine zweite, eigene Zellenlogik hier waere die Stelle, an der PDF und Bildschirm auseinander
  // laufen.
  const matrixAnalysis = buildRequirementAnalysis(firstView.factSkill).filter(
    (a) => a.group === "BASICS" || a.group === "EXPERTISE",
  );
  const requirementMatrix: MatchResultPdfRequirementMatrix | null =
    matrixAnalysis.length > 0 && firstTeamPersons.length > 0
      ? {
          columns: matrixAnalysis.map((a, position) => {
            const constraint = constraintSummary(a.condition);
            const name = conditionLabel(a.condition);
            return {
              label: `R${position + 1}`,
              fullLabel: constraint ? `${name} (${constraint})` : name,
              isMust: a.isMust,
            };
          }),
          rows: firstTeamPersons.map((person) => ({
            personId: person.id,
            personName: person.name,
            cells: matrixAnalysis.map((a) => a.metPersonIds.includes(person.id)),
          })),
        }
      : null;

  const powerColumns = buildPowerColumnGroups(firstView.powers).flatMap((group) =>
    group.elements.map((element) => ({
      element,
      code: ELEMENT_CODES[element] ?? element.slice(0, 3),
      group: group.key as string,
    })),
  );
  const heatmapRows = buildHeatmapRows(firstView.powers, firstTeamPersons);
  const powersMatrix: MatchResultPdfPowersMatrix | null =
    powerColumns.length > 0 && heatmapRows.length > 0
      ? {
          columns: powerColumns,
          rows: heatmapRows.map((row) => ({
            personId: row.personId,
            personName: row.personName,
            cells: powerColumns.map(
              (col) => row.cells.find((cell) => cell.element === col.element)?.value ?? null,
            ),
          })),
        }
      : null;

  // Agenten sind Projektkontext, nie Teammitglieder: kein Rang, kein Score, keine Zeile im Team.
  //
  // Der Schnappschuss hat Vorrang, und seit genesis#320 traegt er die Agenten auch: ein
  // gespeichertes Ergebnis zeigt die, mit denen gerechnet wurde, nicht die von heute. Genesis
  // schreibt den Schnappschuss seitdem als rohe Map, statt ihn durch das Java-Record MatchDesign
  // zu serialisieren, das den Schluessel nicht kennt.
  //
  // Der Schluessel ueberlebt, weil genesis das Design durchreicht - NICHT, weil er dort einen
  // typisierten Platz bekommen haette. Genesis fuehrt seit genesis#408 eine Agentenliste als
  // Instanzdaten, aber `design.agents` bleibt ein Schnappschuss ohne typisierten Platz im Design
  // (@adr SPARK-0034-ai-agents-are-project-context-not-team-members@2).
  //
  // Der Fallback auf den Live-Stand bleibt trotzdem, und zwar dauerhaft: vor genesis#320 geloeste
  // Ergebnisse tragen den Schluessel nie, und ein Backfill waere falsch - die einzige Quelle waere
  // die heutige Agentenliste, nicht die des Laufs (@adr SPARK-0009-match-as-container-results-as-artifacts@1).
  // Er verschwindet erst, wenn es keine Vor-#320-Ergebnisse mehr gibt.
  //
  // Ein Laengen-Check genuegt als Unterscheidung: serializeDesign schreibt "agents" nur, wenn die
  // Liste nicht leer ist - "Schnappschuss hat keine" und "Schluessel fehlt" sind derselbe Fall.
  const snapshotAgents = design.agents ?? [];
  const agentSource = snapshotAgents.length > 0 ? snapshotAgents : (data.agents ?? []);
  // displayAgent bevorzugt die genesis-Zeile in der aktiven Sprache und faellt auf den gespeicherten
  // Schnappschuss zurueck, wenn die Zeile geloescht wurde.
  const agents: MatchResultPdfAgent[] = agentSource.map((a) => ({
    id: a.id,
    ...displayAgent(a, liveAgents.get(a.id)),
  }));

  const rank = companyMode
    ? buildMatchResultPdfRankForCompanies({
        ranked: rankedCompanies,
        thresholds: rankThresholds,
        withDetails: effectiveSections.personDetails,
        withMatrix: effectiveSections.matrices,
        selectedEntryIds: selectedPersonIds,
        formatters: {
          label: companyFormat.requirement,
          actual: companyFormat.actual,
          kindOrder: (condition) => companyFormat.kind(condition).order,
          intl,
        },
      })
    : rankMode
    ? buildMatchResultPdfRank({
        view: firstView,
        persons: firstTeamPersons,
        thresholds: rankThresholds,
        sortBy: rankSortBy,
        elementWeights,
        requirementLeniency: design.requirementLeniency ?? DEFAULT_REQUIREMENT_LENIENCY,
        withDetails: effectiveSections.personDetails,
        withMatrix: effectiveSections.matrices,
        selectedPersonIds,
        teamFitBasis: design.recruitingTeamFit ?? null,
        formatters: {
          label: conditionLabel,
          actual: (condition, actual) => formatConditionActual(condition, actual, labelLookup, intl),
          kindOrder: criterionKindOrder,
          intl,
        },
      })
    : null;

  const now = new Date();
  const title = data.matchTitle || data.projectName || result.name || fallbackTitle;
  const firstTeamStats = teams[0] ?? null;
  const dataQualityNotes = [
    requirementsCount === 0 ? dataQualityLabels.noRequirements : null,
    unknownRequirementsCount > 0 ? dataQualityLabels.unknownRequirements : null,
    firstTeamStats && firstTeamStats.measuredPowerMembers === 0 ? dataQualityLabels.noPowers : null,
  ].filter((note): note is string => note !== null);
  return {
    fileName: fileName(now, title),
    locale,
    mode,
    matchTitle: title,
    projectName: data.projectName || null,
    resultName: result.name ?? null,
    resultCreatedAt: result.createdAt ?? null,
    exportedAt: now.toISOString(),
    candidateCount: result.candidateCount ?? null,
    requirementsCount,
    unknownRequirementsCount,
    dataQualityNotes,
    notes: result.notes ?? null,
    sections: effectiveSections,
    teams,
    agents,
    requirementMatrix: rank ? rank.matrix : requirementMatrix,
    powersMatrix: rank ? null : powersMatrix,
    rank,
  };
}


// Dieselbe Reihenfolge wie auf dem Schirm (RANK_CLUSTERS): erst die Baender, dann die beiden
// Sondergruppen.
const RANK_PICKER_BANDS = ["A", "B", "C", "REVIEW", "UNQUALIFIED"] as const;

// Wer im Dokument steht. Die Bandkoepfe SIND die Voreinstellungen: ein Haken auf "A" heisst
// "bis A", der Haken im Kopf der Sektion heisst "alle". Damit gibt es keinen Preset-Zustand,
// der mit einem Einzelklick in Konflikt geraten koennte - jede Box liest nur aus, was gewaehlt
// ist. Aus der Vorgabe (A+B) ist jede vom Nutzer genannte Voreinstellung ein einzelner Klick.
function CandidatePicker({
  candidates,
  titleId,
  onChange,
  intl,
}: {
  candidates: MatchResultPdfRankCandidate[];
  /** Wie der Gegenstand heisst - Kandidaten oder Unternehmen. */
  titleId: string;
  onChange: (next: ReadonlySet<string>) => void;
  intl: IntlShape;
}) {
  const includedIds = new Set(candidates.filter((c) => c.included).map((c) => c.personId));
  const setMany = (ids: string[], include: boolean) => {
    const next = new Set(includedIds);
    for (const id of ids) {
      if (include) next.add(id);
      else next.delete(id);
    }
    onChange(next);
  };
  const allIds = candidates.map((c) => c.personId);
  const groups = RANK_PICKER_BANDS.map((band) => ({
    band,
    rows: candidates
      .map((candidate, index) => ({ candidate, rank: index + 1 }))
      .filter((row) => row.candidate.band === band),
  })).filter((group) => group.rows.length > 0);

  return (
    <Disclosure
      title={intl.formatMessage({ id: titleId, defaultMessage: "Candidates" })}
      count={`${includedIds.size}/${candidates.length}`}
      defaultOpen
      headerAction={
        <input
          type="checkbox"
          checked={includedIds.size === candidates.length}
          onChange={() => setMany(allIds, includedIds.size !== candidates.length)}
          onClick={(event) => event.stopPropagation()}
          className="h-4 w-4 shrink-0 accent-ink"
          aria-label={intl.formatMessage({ id: "match.pdf.rank.selectAll", defaultMessage: "Select all" })}
        />
      }
    >
      <div className="space-y-3 px-5 py-3">
        {groups.map(({ band, rows }) => {
          const ids = rows.map((row) => row.candidate.personId);
          const selected = ids.filter((id) => includedIds.has(id)).length;
          return (
            <div key={band}>
              <label className="smycles-field-label flex cursor-pointer items-center gap-2 py-1 hover:bg-ink/[0.06]">
                <input
                  type="checkbox"
                  checked={selected === ids.length}
                  ref={(node) => {
                    if (node) node.indeterminate = selected > 0 && selected < ids.length;
                  }}
                  onChange={() => setMany(ids, selected !== ids.length)}
                  className="h-4 w-4 shrink-0 accent-ink"
                />
                <span className="min-w-0 truncate">{rankClusterLabel(band, intl)}</span>
                <span className="ml-auto shrink-0 tabular-nums text-ink-muted">{ids.length}</span>
              </label>
              {rows.map(({ candidate, rank }) => (
                <label
                  key={candidate.personId}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-smycles-meta hover:bg-ink/[0.06]"
                >
                  <input
                    type="checkbox"
                    checked={candidate.included}
                    onChange={() => setMany([candidate.personId], !candidate.included)}
                    className="h-4 w-4 shrink-0 accent-ink"
                  />
                  <span className="w-5 shrink-0 tabular-nums text-ink-muted">{rank}</span>
                  <span className="min-w-0 truncate">{candidate.name}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    </Disclosure>
  );
}

export function MatchResultPdfExportModal({
  open,
  onClose,
  data,
  result,
  rankThresholds = DEFAULT_RANK_THRESHOLDS,
  rankSortBy = "smycles",
}: {
  open: boolean;
  onClose: () => void;
  data: MatchData;
  result: MatchResultPdfSourceResult | null;
  // Der Sichtzustand der Rangliste, so wie er auf dem Schirm steht. Vorgabewerte nur, damit
  // Einzelverwendungen ohne gefuehrtes Panel nicht scheitern.
  rankThresholds?: RankThresholds;
  rankSortBy?: RankSortBy;
}) {
  const intl = useIntl();
  // Beide Hooks haengen an Apollo (Abteilungen, Laender, Branchen) und muessen deshalb HIER
  // laufen: der Renderer-iframe ist ein eigener React-Baum ohne Apollo-Context. Die fertigen
  // Labels reisen als Strings in der Nutzlast mit.
  const labelLookup = useConditionLabelLookup();
  const labelFormatters = useConditionLabelFormatters(intl);
  // Live rows only for the agents this report can name: the saved run's snapshot and the match's
  // own list, never the instance's whole list.
  const reportAgentIds = useMemo(() => {
    const snapshot = result?.design ? (parseDesign(result.design).agents ?? []) : [];
    return [...new Set([...snapshot, ...(data.agents ?? [])].map((a) => a.id))];
  }, [result, data.agents]);
  const { byId: liveAgents } = useAgentsByIds(reportAgentIds);
  // Derselbe Formatierer wie im CompanyRankingPanel - der Bericht darf die Kriterien nicht
  // anders benennen als der Schirm, von dem aus er exportiert wird.
  const { industries } = useRequirementsReferenceData();
  const industryLabelById = useMemo(
    () => new Map(industries.map((industry) => [industry.value, industry.label])),
    [industries],
  );
  const companyFormat = useMemo(
    () => criterionFormatter(intl, industryLabelById),
    [intl, industryLabelById],
  );
  const initial = useMemo(() => readStoredSettings(), []);
  const [sections, setSections] = useState<MatchResultPdfSectionSettings>(initial.sections);
  // null = noch keine Wahl; dann greift die Vorgabe im Builder. Bewusst nicht persistiert und
  // nicht hochgezogen: der Storage-Key ist global und ResultStep behaelt seinen Zustand ueber
  // Ergebniswechsel hinweg - beides wuerde Ids eines anderen Matches wiederherstellen.
  const [selectedPersonIds, setSelectedPersonIds] = useState<ReadonlySet<string> | null>(null);
  const [rendererReady, setRendererReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rendererFrame = useRef<HTMLIFrameElement | null>(null);
  const mode = resolveMatchMode(data.matchMode);
  const hasResultNotes = Boolean(result?.notes?.trim());
  const modeLabelId = matchModeLabelId(mode);
  const modeLabel =
    modeLabelId != null
      ? intl.formatMessage({ id: modeLabelId })
      : intl.formatMessage({ id: "match.pdf.modeUnknown", defaultMessage: "Match result" });
  const payload = useMemo(
    () =>
      result
        ? buildPayload({
            data,
            result,
            locale: intl.locale,
            sections,
            selectedPersonIds,
            rankThresholds,
            rankSortBy,
            intl,
            labelLookup,
            labelFormatters,
            companyFormat,
            liveAgents,
            fallbackTitle: modeLabel,
            dataQualityLabels: {
              noRequirements: intl.formatMessage({
                id: "match.pdf.dataQuality.noRequirements",
                defaultMessage: "No requirements were defined for this result.",
              }),
              unknownRequirements: intl.formatMessage({
                id: "match.pdf.dataQuality.unknownRequirements",
                defaultMessage:
                  "Some saved requirements are from a newer client and are counted but not detailed here.",
              }),
              noPowers: intl.formatMessage({
                id: "match.pdf.dataQuality.noPowers",
                defaultMessage: "No team member has measured Powers in this result.",
              }),
            },
          })
        : null,
    [
      data,
      selectedPersonIds,
      rankThresholds,
      rankSortBy,
      intl,
      labelFormatters,
      labelLookup,
      companyFormat,
      liveAgents,
      result,
      sections,
    ],
  );
  // Nur noch die Unternehmens-Rangliste kappt ihre Liste ueber einen Schalter. Die Personen-Rangliste
  // waehlt stattdessen aus, WER im Dokument steht.
  // Die Schalterliste liest die fertige Nutzlast, nicht eine zweite Quelle. Vorher fragte das
  // Gate den Live-Stand (data.agents) und die Nutzlast den Schnappschuss - bei einem
  // gespeicherten Ergebnis wurde der Abschnitt deshalb angeboten und war dann leer. Aus
  // derselben Liste zu lesen, die das Dokument rendert, macht dieses Auseinanderlaufen unmoeglich.
  const available = sectionsForMode(mode);
  const relevantSections = available.filter(
    (section) =>
      section !== "summary" &&
      (section !== "notes" || hasResultNotes) &&
      (section !== "agents" || (payload?.agents.length ?? 0) > 0),
  );

  function postToRenderer(message: MatchResultRendererRequest) {
    rendererFrame.current?.contentWindow?.postMessage(message, window.location.origin);
  }

  useEffect(() => {
    storeSettings(sections);
  }, [sections]);

  useEffect(() => {
    if (!open) return;
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.source !== rendererFrame.current?.contentWindow) return;
      if (!isMatchResultRendererResponse(event.data)) return;
      if (event.data.type === "match-result-renderer-ready") {
        setRendererReady(true);
      } else {
        setError(intl.formatMessage({ id: "match.pdf.downloadFailed", defaultMessage: "Download failed." }));
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [intl, open]);

  useEffect(() => {
    if (!open) {
      setRendererReady(false);
      return;
    }
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!payload || !rendererReady) return;
    const timer = setTimeout(() => {
      postToRenderer({ type: "match-result-render", payload });
    }, 300);
    return () => clearTimeout(timer);
  }, [payload, rendererReady]);

  function toggleSection(section: MatchResultPdfSection) {
    setSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  function download() {
    if (!payload) return;
    setError(null);
    postToRenderer({ type: "match-result-download", payload });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismiss="light"
      large
      fillViewport
      title={<FormattedMessage id="match.pdf.exportTitle" defaultMessage="PDF export" />}
      footer={
        <Button variant="primary" onClick={download} disabled={!payload || !rendererReady}>
          <FormattedMessage id="match.pdf.download" defaultMessage="Download PDF" />
        </Button>
      }
    >
      <div className="grid min-h-0 flex-1 grid-cols-[17rem_minmax(0,1fr)] gap-4">
        <aside className="min-h-0 overflow-auto border-r border-ink/10 pr-4">
          <div className="font-serif text-smycles-display-s leading-tight">{data.matchTitle || data.projectName || modeLabel}</div>
          <div className="smycles-field-label mt-2">{modeLabel}</div>
          {error ? <div className="box-flat mt-4 bg-ink/[0.06] p-2 text-smycles-meta text-ink">{error}</div> : null}
          <div className="mt-6">
            <div className="smycles-field-label mb-2">
              <FormattedMessage id="match.pdf.sections" defaultMessage="Sections" />
            </div>
            <div className="space-y-2">
              {relevantSections.map((section) => (
                <div key={section} className="smycles-field-value flex items-center justify-between gap-3 text-ink">
                  <span>{intl.formatMessage(sectionLabel(section, mode))}</span>
                  <Switch
                    checked={sections[section]}
                    onChange={() => toggleSection(section)}
                    // Personen-Details vertiefen den Team-Abschnitt; ohne Roster gibt es nichts
                    // zu vertiefen, also wird die Zeile hier stumpf statt still wirkungslos.
                    disabled={section === "personDetails" && available.includes("team") && !sections.team}
                    ariaLabel={intl.formatMessage(sectionLabel(section, mode))}
                  />
                </div>
              ))}
            </div>
          </div>
          {payload?.rank ? (
            <div className="mt-6">
              <CandidatePicker
                candidates={payload.rank.candidates}
                titleId={
                  mode === "RANK_COMPANIES"
                    ? "match.result.insights.subjectCompanies"
                    : "match.pdf.rank.candidates"
                }
                onChange={setSelectedPersonIds}
                intl={intl}
              />
            </div>
          ) : null}
        </aside>
        <div className="min-h-0 bg-ink/[0.06]">
          <iframe
            ref={rendererFrame}
            src={matchResultRendererDocumentUrl()}
            title={intl.formatMessage({ id: "match.pdf.previewTitle", defaultMessage: "Match result PDF preview" })}
            className="h-full w-full border-0"
          />
        </div>
      </div>
    </Modal>
  );
}
